import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';

import { demographicsApi, type DemographicsAggregate } from './api';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { useT } from '@/lib/i18n/useT';

/**
 * Caso 09 — Bucketed demographic snapshot. Drops into EventDetailPage and
 * EventReportPage. Hides itself if no demographic interactions exist yet so
 * a brand-new event doesn't show a wall of zeros.
 *
 * Buckets:
 *  - ageRange  (13-17, 18-24, 25-34, 35-44, 45-54, 55+)
 *  - gender    (female, male, non-binary, other, undisclosed)
 *  - city / state / language  (top 5 each)
 */
export function DemographicsPanel({
  eventId,
  eventIds,
  variant = 'event',
}: {
  eventId?: number;
  /**
   * For `variant='brand'`: aggregate demographics across these events
   * (typically every event belonging to the active brand). Ignored for
   * other variants.
   */
  eventIds?: number[];
  /**
   * `event` queries by `eventId` (required), `workspace` aggregates the
   * caller's billing root, `brand` fans out across `eventIds`.
   */
  variant?: 'event' | 'workspace' | 'brand';
}) {
  const t = useT();

  // Single-event / workspace mode — one request, hidden when zero.
  const singleQ = useQuery({
    queryKey: ['demographics', variant, eventId ?? 0],
    queryFn: () =>
      variant === 'event' && eventId
        ? demographicsApi.byEvent(eventId)
        : demographicsApi.workspace(),
    enabled:
      variant !== 'brand' &&
      (variant === 'workspace' || (eventId !== undefined && eventId > 0)),
  });

  // Brand mode — fan-out across the brand's events and reduce in memory.
  // The `/co-contacts/demographics` workspace mode doesn't take a brand
  // filter, so this is the only way to avoid leaking cross-brand counts.
  const brandEventIds = variant === 'brand' ? eventIds ?? [] : [];
  const brandQs = useQueries({
    queries: brandEventIds.map((id) => ({
      queryKey: ['demographics', 'event', id] as const,
      queryFn: () => demographicsApi.byEvent(id),
      enabled: variant === 'brand' && id > 0,
    })),
  });
  const brandAggregate = useMemo(() => {
    if (variant !== 'brand') return null;
    const results = brandQs
      .map((q) => q.data)
      .filter((d): d is DemographicsAggregate => !!d);
    return mergeAggregates(results);
  }, [brandQs, variant]);
  const brandLoading =
    variant === 'brand' && brandQs.some((q) => q.isLoading);

  const isLoading = variant === 'brand' ? brandLoading : singleQ.isLoading;
  const data = variant === 'brand' ? brandAggregate : singleQ.data;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            {t('demographics.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24" />
        </CardContent>
      </Card>
    );
  }

  // Hide on empty — avoids a card full of 0%s for brand-new events.
  // Exception: in `brand` mode the user is browsing the Insights page
  // and benefits from an explicit empty state ("brand has no events" or
  // "events have no demographic responses yet") rather than a silent
  // disappearance.
  if (!data || data.total === 0) {
    if (variant === 'brand') {
      const fannedOut = (eventIds ?? []).length;
      return (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              {t('demographics.title')}
            </CardTitle>
            <CardDescription>
              {fannedOut === 0
                ? t('demographics.brand.noEvents')
                : t('demographics.brand.noData', { count: String(fannedOut) })}
            </CardDescription>
          </CardHeader>
        </Card>
      );
    }
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          {t('demographics.title')}
        </CardTitle>
        <CardDescription>
          {t('demographics.subtitle', { count: String(data.total) })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Bucket
          label={t('demographics.ageRange')}
          buckets={data.ageRange}
          total={data.total}
          orderHint={['13-17', '18-24', '25-34', '35-44', '45-54', '55+']}
          labelMap={(k) => k}
        />
        <Bucket
          label={t('demographics.gender')}
          buckets={data.gender}
          total={data.total}
          orderHint={['female', 'male', 'non-binary', 'other', 'undisclosed']}
          labelMap={(k) => t(`demographics.genderValue.${k}` as const)}
        />
        <Bucket
          label={t('demographics.ethnicity')}
          buckets={top(data.ethnicity, 8)}
          total={data.total}
          labelMap={(k) => k}
        />
        <Bucket
          label={t('demographics.state')}
          buckets={top(data.state, 5)}
          total={data.total}
          labelMap={(k) => k}
        />
        <Bucket
          label={t('demographics.city')}
          buckets={top(data.city, 5)}
          total={data.total}
          labelMap={(k) => k}
        />
        <Bucket
          label={t('demographics.language')}
          buckets={top(data.language, 5)}
          total={data.total}
          labelMap={(k) => k}
        />
      </CardContent>
    </Card>
  );
}

function Bucket({
  label,
  buckets,
  total,
  orderHint,
  labelMap,
}: {
  label: string;
  buckets: Record<string, number>;
  total: number;
  /** Render buckets in this exact order when set; otherwise descending count. */
  orderHint?: string[];
  labelMap: (key: string) => string;
}) {
  const entries = Object.entries(buckets);
  if (entries.length === 0) return null;
  const ordered = orderHint
    ? orderHint
        .filter((k) => buckets[k] > 0)
        .map((k) => [k, buckets[k]] as const)
    : entries.sort((a, b) => b[1] - a[1]);

  return (
    <div>
      <div className="mb-1 text-xs uppercase tracking-wide text-[var(--color-fg-muted)]">
        {label}
      </div>
      <div className="space-y-1">
        {ordered.map(([key, count]) => {
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={key} className="flex items-center gap-2 text-xs">
              <span className="w-32 truncate text-[var(--color-fg-primary)]">
                {labelMap(key)}
              </span>
              <div className="flex-1 rounded bg-[var(--color-bg-elevated)] h-2 overflow-hidden">
                <div
                  className="h-2 rounded bg-[var(--color-accent)]"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="w-12 text-right tabular-nums text-[var(--color-fg-muted)]">
                {pct}%
              </span>
              <span className="w-8 text-right tabular-nums text-[var(--color-fg-subtle)]">
                {count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Sum every bucket field across a list of `DemographicsAggregate`s.
 * Used by the brand-variant fan-out to combine per-event responses
 * into a single brand-wide snapshot without going through the
 * backend's workspace endpoint.
 */
function mergeAggregates(
  items: DemographicsAggregate[],
): DemographicsAggregate | null {
  if (items.length === 0) return null;
  const out: DemographicsAggregate = {
    total: 0,
    ageRange: {},
    gender: {},
    ethnicity: {},
    city: {},
    state: {},
    language: {},
  };
  const fields: Array<keyof Omit<DemographicsAggregate, 'total'>> = [
    'ageRange',
    'gender',
    'ethnicity',
    'city',
    'state',
    'language',
  ];
  for (const it of items) {
    out.total += it.total ?? 0;
    for (const f of fields) {
      for (const [k, v] of Object.entries(it[f] ?? {})) {
        out[f][k] = (out[f][k] ?? 0) + (v as number);
      }
    }
  }
  return out;
}

/** Keep only the top N entries by count; useful for high-cardinality buckets. */
function top(
  bucket: Record<string, number>,
  n: number,
): Record<string, number> {
  const out: Record<string, number> = {};
  Object.entries(bucket)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .forEach(([k, v]) => {
      out[k] = v;
    });
  return out;
}
